/**
 * ENVIRONMENT: PRODUCTION
 * COMPONENT: B2C Central Operations & Payin Overview
 * PATH: src/routes/overviewPayin/OverviewCardByDate.js
 */

import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Card,
  Col,
  Row,
  Button,
  Spin,
  Progress,
  Tag,
} from "antd";
import DatePicker from "components/DatePicker";
import dayjs from "dayjs";
import { formatCurrency } from "utils/common";
import { getOverviewadminSummaryPayin } from "requests/statistic";
import { toast } from "react-toast";
import {
  ThunderboltFilled,
  CheckCircleFilled,
  ClockCircleFilled,
  MobileOutlined,
  AppstoreOutlined,
  InsuranceOutlined,
  UserOutlined,
  CreditCardOutlined,
  GiftOutlined,
  BankOutlined,
} from "@ant-design/icons";
import "./OverviewCardByDate.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const OverviewCardByDate = () => {
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [mode, setMode] = useState("today");
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(false);
  const [categorySummary, setCategorySummary] = useState([]);
  
  // Independent Gift Voucher State
  const [giftVoucher, setGiftVoucher] = useState({
    successCount: 0,
    successAmt: 0,
    pendingAmt: 0,
    failedAmt: 0,
  });

  const availableModes = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "last7days", label: "Last 7 Days" },
    { key: "last30days", label: "Last 30 Days" },
  ];

  useEffect(() => {
    getData(dates);
  }, [dates]);

  const getData = async (selectedDates) => {
    try {
      setLoading(true);
      const filters = {
        startDate: selectedDates[0].format("YYYY-MM-DD"),
        endDate: selectedDates[1].format("YYYY-MM-DD"),
      };

      const response = await getOverviewadminSummaryPayin(filters);

      if (response && (response.status === "1" || response.status === 1)) {
        setUserData(response.user_summary || {});
        setCategorySummary(response.category_status_summary || []);

        setGiftVoucher({
          successCount: Number(response.no_of_success_gift_voucher) || 0,
          successAmt: Number(response.gift_voucher_success_amt) || 0,
          pendingAmt: Number(response.gift_voucher_pending_amt) || 0,
          failedAmt: Number(response.gift_voucher_failed_amt) || 0,
        });
      } else {
        toast.error(response?.message || "Failed to fetch overview analytics.");
      }
    } catch (err) {
      toast.error("Failed to synchronize B2C switch metrics.");
    } finally {
      setLoading(false);
    }
  };

  // Safe category matcher
  const getCategoryData = (name) => {
    if (!categorySummary || !Array.isArray(categorySummary)) return null;
    const cleanTarget = name.toLowerCase().replace(/\s+/g, "");
    return categorySummary.find((cat) => {
      const current = (cat.bill_category_name || "").toLowerCase().replace(/\s+/g, "");
      return current === cleanTarget || current.includes(cleanTarget);
    });
  };

  const billPayments = getCategoryData("Bill Payment") || getCategoryData("Bill Payments");
  const recharge = getCategoryData("Recharge");
  const prepaidCards = getCategoryData("Prepaid Card") || getCategoryData("Prepaid Cards");
  const insuranceData = getCategoryData("Insurance");
  const consumerLoans = getCategoryData("Consumer Loans") || getCategoryData("Loans");

  // Currency Formatter helper
  const renderRupee = (val) => {
    const num = Number(val) || 0;
    try {
      if (typeof formatCurrency === "function") {
        return formatCurrency(num);
      }
    } catch (e) {
      // Fallback
    }
    return `₹${num.toLocaleString("en-IN")}`;
  };

  // Aggregated B2C Top-Level Metrics
  const aggregatedStats = useMemo(() => {
    const bpSuccessAmt = Number(billPayments?.status?.success?.total_amount) || 0;
    const rcSuccessAmt = Number(recharge?.status?.success?.total_amount) || 0;
    const cardSuccessAmt = Number(prepaidCards?.status?.success?.total_amount) || 0;
    const gvSuccessAmt = Number(giftVoucher.successAmt) || 0;
    const insSuccessAmt = Number(insuranceData?.status?.success?.total_amount) || 0;
    const totalSuccessVolume = bpSuccessAmt + rcSuccessAmt + cardSuccessAmt + gvSuccessAmt + insSuccessAmt;

    const bpSuccessCount = Number(billPayments?.status?.success?.count) || 0;
    const rcSuccessCount = Number(recharge?.status?.success?.count) || 0;
    const cardSuccessCount = Number(prepaidCards?.status?.success?.count) || 0;
    const gvSuccessCount = Number(giftVoucher.successCount) || 0;
    const insSuccessCount = Number(insuranceData?.status?.success?.count) || 0;
    const totalSuccessTxns = bpSuccessCount + rcSuccessCount + cardSuccessCount + gvSuccessCount + insSuccessCount;

    const bpPendingAmt = Number(billPayments?.status?.pending?.total_amount) || 0;
    const rcPendingAmt = Number(recharge?.status?.pending?.total_amount) || 0;
    const cardPendingAmt = Number(prepaidCards?.status?.pending?.total_amount) || 0;
    const gvPendingAmt = Number(giftVoucher.pendingAmt) || 0;
    const insPendingAmt = Number(insuranceData?.status?.pending?.total_amount) || 0;
    const totalPendingVolume = bpPendingAmt + rcPendingAmt + cardPendingAmt + gvPendingAmt + insPendingAmt;

    const bpFailedAmt = Number(billPayments?.status?.failed?.total_amount) || 0;
    const rcFailedAmt = Number(recharge?.status?.failed?.total_amount) || 0;
    const cardFailedAmt = Number(prepaidCards?.status?.failed?.total_amount) || 0;
    const gvFailedAmt = Number(giftVoucher.failedAmt) || 0;
    const insFailedAmt = Number(insuranceData?.status?.failed?.total_amount) || 0;
    const totalFailedVolume = bpFailedAmt + rcFailedAmt + cardFailedAmt + gvFailedAmt + insFailedAmt;

    const totalTxnVolume = totalSuccessVolume + totalPendingVolume + totalFailedVolume;
    
    // 1. Safe success ratio calculation (0.0% when volume is zero)
    const successRatio = totalTxnVolume > 0 
      ? ((totalSuccessVolume / totalTxnVolume) * 100).toFixed(1) 
      : "0.0";

    // 2. Safe fallback consumer counts
    const activeUsers = 
      userData?.user?.active ?? 
      userData?.active_users ?? 
      userData?.total_users ?? 
      userData?.total ?? 
      0;

    const totalRegistered = 
      userData?.user?.total ?? 
      userData?.total_users ?? 
      userData?.total ?? 
      0;

    return {
      totalSuccessVolume,
      totalSuccessTxns,
      totalPendingVolume,
      totalFailedVolume,
      successRatio,
      activeUsers,
      totalRegistered,
    };
  }, [billPayments, recharge, prepaidCards, giftVoucher, insuranceData, userData]);

  const onSetDatesByDatePicker = (newDates) => {
    if (newDates && newDates.length === 2) {
      setMode("custom");
      setDates(newDates);
    }
  };

  const onSetDatesByMode = (selectedMode) => {
    setMode(selectedMode);
    if (selectedMode === "today") {
      setDates([dayjs(), dayjs()]);
    } else if (selectedMode === "yesterday") {
      setDates([dayjs().subtract(1, "day"), dayjs().subtract(1, "day")]);
    } else if (selectedMode === "last7days") {
      setDates([dayjs().subtract(7, "day"), dayjs()]);
    } else if (selectedMode === "last30days") {
      setDates([dayjs().subtract(30, "day"), dayjs()]);
    }
  };

  return (
    <div className="enterprise-b2c-overview">
      {/* Top Filter and Telemetry Bar */}
      <div className="overview-header-panel">
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} lg={12}>
            <div className="telemetry-branding">
              <span className="live-status-dot" />
              <div>
                <Title level={4} className="panel-title">
                  B2C Switch Telemetry &amp; Operations Overview
                </Title>
                <Text type="secondary" className="panel-subtitle">
                  Isolated rails: Bharat Connect (BBPS), Telecom, Prepaid Cards, Vouchers, Insurance &amp; Loans
                </Text>
              </div>
            </div>
          </Col>

          <Col xs={24} lg={12} className="filter-controls-col">
            <div className="filter-pills-wrap">
              {availableModes.map((item) => (
                <Button
                  key={item.key}
                  type={mode === item.key ? "primary" : "default"}
                  onClick={() => onSetDatesByMode(item.key)}
                  className={`filter-preset-btn ${mode === item.key ? "active" : ""}`}
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <RangePicker
              value={dates}
              onChange={onSetDatesByDatePicker}
              format="YYYY-MM-DD"
              className="overview-range-picker"
              allowClear={false}
            />
          </Col>
        </Row>
      </div>

      <Spin spinning={loading} tip="Reconciling discrete service rails...">
        {/* Executive KPI Summary Row */}
        <Row gutter={[16, 16]} className="kpi-metric-row">
          <Col xs={24} sm={12} lg={6}>
            <Card className="executive-kpi-card" bordered={false}>
              <div className="kpi-icon-container bg-blue-tint">
                <ThunderboltFilled style={{ color: "#0284c7" }} />
              </div>
              <div className="kpi-meta">
                <Text className="kpi-label">Gross Success Volume</Text>
                <div className="kpi-value-text">{renderRupee(aggregatedStats.totalSuccessVolume)}</div>
                <div className="kpi-subtag">
                  <Tag color="cyan">{aggregatedStats.totalSuccessTxns} Settled Orders</Tag>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card className="executive-kpi-card" bordered={false}>
              <div className="kpi-icon-container bg-emerald-tint">
                <CheckCircleFilled style={{ color: "#10b981" }} />
              </div>
              <div className="kpi-meta">
                <Text className="kpi-label">Overall Switch Success</Text>
                <div className="kpi-value-text text-emerald">{aggregatedStats.successRatio}%</div>
                <div className="kpi-subtag">
                  <Progress
                    percent={parseFloat(aggregatedStats.successRatio)}
                    size="small"
                    showInfo={false}
                    strokeColor="#10b981"
                  />
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card className="executive-kpi-card" bordered={false}>
              <div className="kpi-icon-container bg-amber-tint">
                <ClockCircleFilled style={{ color: "#f59e0b" }} />
              </div>
              <div className="kpi-meta">
                <Text className="kpi-label">In-Flight Pending Float</Text>
                <div className="kpi-value-text text-amber">{renderRupee(aggregatedStats.totalPendingVolume)}</div>
                <div className="kpi-subtag">
                  <Text type="secondary" className="kpi-sub-label">Awaiting Rail Acknowledgment</Text>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card className="executive-kpi-card" bordered={false}>
              <div className="kpi-icon-container bg-purple-tint">
                <UserOutlined style={{ color: "#8b5cf6" }} />
              </div>
              <div className="kpi-meta">
                <Text className="kpi-label">Active Consumer Base</Text>
                <div className="kpi-value-text">
                  {Number(aggregatedStats.activeUsers).toLocaleString()}
                </div>
                <div className="kpi-subtag">
                  <Text type="secondary" className="kpi-sub-label">
                    Total Registered: {Number(aggregatedStats.totalRegistered).toLocaleString()}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Structured Service Channel Cards */}
        <div className="section-title-wrap">
          <Title level={4} className="section-heading">
            Independent B2C Product Rails &amp; Gateways
          </Title>
          <Text type="secondary">
            Operational telemetry partitioned by provider switch and clearing gateway
          </Text>
        </div>

        <Row gutter={[16, 16]}>
          {/* Rail 1: Bharat Bill Payment System (BBPS) & NCMC Transit */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-blue-tint">
                    <AppstoreOutlined style={{ color: "#0284c7" }} />
                  </div>
                  <div>
                    <span className="channel-title">Bharat Connect (BBPS) &amp; NCMC Transit</span>
                    <span className="channel-desc">Electricity, Water, Gas, FASTag, Municipal &amp; Metro Card Recharges</span>
                  </div>
                </div>
                <Tag color="blue" className="rail-protocol-tag">NPCI BBPS Switch</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Successful Clearance</span>
                  <span className="stat-amount">{renderRupee(billPayments?.status?.success?.total_amount)}</span>
                  <span className="stat-count">{billPayments?.status?.success?.count || 0} Transactions</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">In-Flight Acknowledgment</span>
                  <span className="stat-amount">{renderRupee(billPayments?.status?.pending?.total_amount)}</span>
                  <span className="stat-count">Pending Clearing</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Failed / Auto-Refund</span>
                  <span className="stat-amount">{renderRupee(billPayments?.status?.failed?.total_amount)}</span>
                  <span className="stat-count">Auto Reversals</span>
                </div>
              </div>
            </Card>
          </Col>

          {/* Rail 2: Mobile & DTH Telecom Recharges */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-emerald-tint">
                    <MobileOutlined style={{ color: "#10b981" }} />
                  </div>
                  <div>
                    <span className="channel-title">Mobile &amp; DTH Telecom Recharges</span>
                    <span className="channel-desc">Prepaid recharge packs, validity, data boosters &amp; DTH top-ups</span>
                  </div>
                </div>
                <Tag color="green" className="rail-protocol-tag">Operator Direct Switch</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Successful Clearance</span>
                  <span className="stat-amount">{renderRupee(recharge?.status?.success?.total_amount)}</span>
                  <span className="stat-count">{recharge?.status?.success?.count || 0} Transactions</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">In-Flight Processing</span>
                  <span className="stat-amount">{renderRupee(recharge?.status?.pending?.total_amount)}</span>
                  <span className="stat-count">Pending Telco Ping</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Failed Recharges</span>
                  <span className="stat-amount">{renderRupee(recharge?.status?.failed?.total_amount)}</span>
                  <span className="stat-count">Instant Reversals</span>
                </div>
              </div>
            </Card>
          </Col>

          {/* Rail 3: RuPay Prepaid Cards */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-indigo-tint">
                    <CreditCardOutlined style={{ color: "#4f46e5" }} />
                  </div>
                  <div>
                    <span className="channel-title">Prepaid Card Issuance</span>
                    <span className="channel-desc">RuPay PPI prepaid virtual &amp; physical cards, loads &amp; tap</span>
                  </div>
                </div>
                <Tag color="geekblue" className="rail-protocol-tag">RuPay / PPI Rail</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Cards Issued &amp; Loaded</span>
                  <span className="stat-amount">{renderRupee(prepaidCards?.status?.success?.total_amount)}</span>
                  <span className="stat-count">{prepaidCards?.status?.success?.count || 0} Active Cards</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">KYC / In-Issuance</span>
                  <span className="stat-amount">{renderRupee(prepaidCards?.status?.pending?.total_amount)}</span>
                  <span className="stat-count">Awaiting Dispatch</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Failed Issuances</span>
                  <span className="stat-amount">{renderRupee(prepaidCards?.status?.failed?.total_amount)}</span>
                  <span className="stat-count">KYC Rejections</span>
                </div>
              </div>
            </Card>
          </Col>

          {/* Rail 4: Digital Brand Gift Vouchers */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-purple-tint">
                    <GiftOutlined style={{ color: "#9333ea" }} />
                  </div>
                  <div>
                    <span className="channel-title">Digital Gift Vouchers</span>
                    <span className="channel-desc">Direct brand e-gift vouchers (Amazon, Zomato, Domino's, etc.)</span>
                  </div>
                </div>
                <Tag color="purple" className="rail-protocol-tag">Brand Aggregator Rail</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Vouchers Delivered</span>
                  <span className="stat-amount">{renderRupee(giftVoucher.successAmt)}</span>
                  <span className="stat-count">{giftVoucher.successCount} Codes Dispatched</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">Issuance In Queue</span>
                  <span className="stat-amount">{renderRupee(giftVoucher.pendingAmt)}</span>
                  <span className="stat-count">Awaiting Pin API</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Failed Vouchers</span>
                  <span className="stat-amount">{renderRupee(giftVoucher.failedAmt)}</span>
                  <span className="stat-count">Unfulfilled / Refunded</span>
                </div>
              </div>
            </Card>
          </Col>

          {/* Rail 5: Consumer Insurance Services */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-amber-tint">
                    <InsuranceOutlined style={{ color: "#f59e0b" }} />
                  </div>
                  <div>
                    <span className="channel-title">Consumer Insurance Services</span>
                    <span className="channel-desc">Wellness &amp; Health, Two-Wheeler &amp; Car Insurance covers</span>
                  </div>
                </div>
                <Tag color="gold" className="rail-protocol-tag">IRDAI Broking Partner</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Policies Issued</span>
                  <span className="stat-amount">{renderRupee(insuranceData?.status?.success?.total_amount)}</span>
                  <span className="stat-count">{insuranceData?.status?.success?.count || 0} Active Covers</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">Inspection &amp; Review</span>
                  <span className="stat-amount">{renderRupee(insuranceData?.status?.pending?.total_amount)}</span>
                  <span className="stat-count">Proposal Submitted</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Rejected Proposals</span>
                  <span className="stat-amount">{renderRupee(insuranceData?.status?.failed?.total_amount)}</span>
                  <span className="stat-count">Underwriting Declined</span>
                </div>
              </div>
            </Card>
          </Col>

          {/* Rail 6: Consumer Credit & Instant Loans */}
          <Col xs={24} xl={12}>
            <Card className="channel-detail-card" bordered={false}>
              <div className="channel-card-header">
                <div className="channel-brand-info">
                  <div className="channel-badge-avatar bg-teal-tint">
                    <BankOutlined style={{ color: "#0d9488" }} />
                  </div>
                  <div>
                    <span className="channel-title">Consumer Credit &amp; Instant Loans</span>
                    <span className="channel-desc">Instant personal loans, credit line &amp; credit score evaluations</span>
                  </div>
                </div>
                <Tag color="cyan" className="rail-protocol-tag">NBFC Lending Switch</Tag>
              </div>

              <div className="channel-stats-grid">
                <div className="channel-stat-box stat-success">
                  <span className="stat-label">Disbursed Volume</span>
                  <span className="stat-amount">{renderRupee(consumerLoans?.status?.success?.total_amount)}</span>
                  <span className="stat-count">{consumerLoans?.status?.success?.count || 0} Accounts Funded</span>
                </div>

                <div className="channel-stat-box stat-pending">
                  <span className="stat-label">Credit Underwriting</span>
                  <span className="stat-amount">{renderRupee(consumerLoans?.status?.pending?.total_amount)}</span>
                  <span className="stat-count">Bureau Checks Active</span>
                </div>

                <div className="channel-stat-box stat-failed">
                  <span className="stat-label">Declined Applications</span>
                  <span className="stat-amount">{renderRupee(consumerLoans?.status?.failed?.total_amount)}</span>
                  <span className="stat-count">Risk / Score Mismatch</span>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default OverviewCardByDate;